import { fetchApi } from "~/lib/api";
import type {
  AdminGetOrdersParams,
  AdminOrder,
  AnonymousDraftOrderUpdate,
  ChildInfo,
  CustomerGetOrdersParams,
  CustomerOrder,
  DraftOrderUpdate,
  JobQueue,
  Order,
  OrderDraft,
  OrderReviewSubmitRequest,
  PaginatedQueryParams,
  Pagination,
  SharedOrder,
  StartJobRequest,
} from "~/types";
import { serializeFilterParams } from "./utils";

export const adminOrdersService = {
  /**
   * Get orders with optional filters and pagination (Admin)
   */
  async getOrders(params: AdminGetOrdersParams = {}) {
    const query = serializeFilterParams(params);
    const endpoint = `/admin/orders${query}`;
    return fetchApi<Pagination<AdminOrder>>(endpoint);
  },

  /**
   * Get a single order by ID (Admin)
   */
  async getOrder(orderId: number) {
    return fetchApi<AdminOrder>(`/admin/orders/${orderId}`);
  },

  /**
   * Delete an order (soft delete) (Admin)
   */
  async deleteOrder(orderId: number) {
    return fetchApi<{ message: string; id: number }>(
      `/admin/orders/${orderId}`,
      {
        method: "DELETE",
      },
    );
  },

  /**
   * Restore a deleted order (Admin)
   */
  async restoreOrder(orderId: number) {
    return fetchApi<{ message: string; order: AdminOrder }>(
      `/admin/orders/${orderId}/restore`,
      {
        method: "POST",
      },
    );
  },

  /**
   * Update an order (Admin)
   */
  async updateOrder(orderId: number, data: Partial<Order>) {
    return fetchApi<AdminOrder>(`/admin/orders/${orderId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  /**
   * Permanently delete an order (hard delete)
   */
  async permanentDeleteOrder(orderId: number) {
    return fetchApi<{ message: string; id: number }>(
      `/admin/orders/${orderId}/permanent`,
      {
        method: "DELETE",
      },
    );
  },

  /**
   * Start a job for an order
   */
  async startJob(orderId: number, options: StartJobRequest = {}) {
    return fetchApi<JobQueue>(`/admin/orders/${orderId}/start-job`, {
      method: "POST",
      body: JSON.stringify(options),
    });
  },

  /**
   * Generate artwork for an order
   */
  async generateArtwork(orderId: number) {
    return fetchApi<{ message: string; artworkFilePath: string }>(
      `/admin/orders/${orderId}/generate-artwork`,
      {
        method: "POST",
      },
    );
  },

  /**
   * Delete artwork for an order
   */
  async deleteArtwork(orderId: number) {
    return fetchApi<{ message: string }>(`/admin/orders/${orderId}/artwork`, {
      method: "DELETE",
    });
  },

  /**
   * Transition: Mark order as completed
   */
  async markCompleted(orderId: number) {
    return fetchApi<AdminOrder>(
      `/admin/orders/${orderId}/transitions/mark-completed`,
      {
        method: "POST",
      },
    );
  },

  /**
   * Transition: Cancel order
   */
  async cancelOrder(orderId: number, reason?: string) {
    return fetchApi<AdminOrder>(`/admin/orders/${orderId}/transitions/cancel`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },

  /**
   * Transition: Submit order without payment
   */
  async submitWithoutPayment(orderId: number) {
    return fetchApi<AdminOrder>(
      `/admin/orders/${orderId}/transitions/submit-without-payment`,
      {
        method: "POST",
      },
    );
  },

  /**
   * Transition: Move back to in progress
   */
  async moveToInProgress(orderId: number) {
    return fetchApi<AdminOrder>(
      `/admin/orders/${orderId}/transitions/move-to-in-progress`,
      {
        method: "POST",
      },
    );
  },

  /**
   * Transition: Place draft order (draft -> pending or in_progress for authenticated users)
   */
  async placeOrder(orderId: number) {
    return fetchApi<AdminOrder>(
      `/admin/orders/${orderId}/transitions/place`,
      {
        method: "POST",
      },
    );
  },

  /**
   * Resend the customer-order-completed email
   */
  async resendOrderCompletedEmail(orderId: number) {
    return fetchApi<AdminOrder>(
      `/admin/orders/${orderId}/resend-order-completed`,
      {
        method: "POST",
      },
    );
  },

  /**
   * Update the order's public token
   */
  async updatePublicToken(orderId: number, publicToken: string | null) {
    return fetchApi<AdminOrder>(`/admin/orders/${orderId}/public-token`, {
      method: "PUT",
      body: JSON.stringify({ publicToken }),
    });
  },

  /**
   * Get anonymous draft order by GUID (Admin)
   */
  async getAnonymousDraftOrder(guid: string) {
    return fetchApi<AdminOrder>(`/admin/orders/draft/${guid}`);
  },
};

/**
 * Customer orders service - for authenticated customers
 */
export const customerOrdersService = {
  /**
   * Get all customer's orders
   */
  async getOrders(filters: CustomerGetOrdersParams = {}) {
    const query = serializeFilterParams(filters);
    const endpoint = `/customer/orders${query}`;
    return fetchApi<Pagination<Order>>(endpoint);
  },

  /**
   * Get customer's own order by ID
   */
  async getOrder(orderId: number) {
    return fetchApi<CustomerOrder>(`/customer/orders/${orderId}`);
  },

  /**
   * Get shared order by ID (for shared song pages)
   */
  async getSharedOrder(orderId: number) {
    return fetchApi<SharedOrder>(`/customer/song-sharing/order/${orderId}`);
  },

  /**
   * Create a new order (convert draft to pending)
   */
  async createOrder(): Promise<Order> {
    return fetchApi<Order>("/customer/orders", {
      method: "POST",
    });
  },

  /**
   * Cancel a customer's order with optional reason
   */
  async cancelOrder(orderId: number, reason?: string) {
    return fetchApi<Order>(`/customer/orders/${orderId}/cancel`, {
      method: "PUT",
      body: JSON.stringify({ reason }),
    });
  },

  /**
   * Get customer's draft order
   */
  async getDraft() {
    return fetchApi<Order>("/customer/orders/draft");
  },

  /**
   * Upsert customer's draft order
   */
  async upsertDraft(data: DraftOrderUpdate) {
    return fetchApi<Order>("/customer/orders/draft", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  /**
   * Get anonymous draft order
   */
  async getAnonymousDraft(anonymousId: string) {
    return fetchApi<OrderDraft | null>(
      `/anonymous/orders/draft/${anonymousId}`,
    );
  },

  /**
   * Update anonymous draft order
   */
  async upsertAnonymousDraft(
    anonymousId: string,
    order: AnonymousDraftOrderUpdate,
  ) {
    return fetchApi<OrderDraft>(`/anonymous/orders/draft/${anonymousId}`, {
      method: "PUT",
      body: JSON.stringify(order),
    });
  },

  /**
   * Get customer's previous children info
   */
  async getChildrenInfo(params: PaginatedQueryParams = {}) {
    const query = serializeFilterParams(params);
    const endpoint = `/customer/orders/children-info${query}`;
    return fetchApi<Pagination<ChildInfo>>(endpoint);
  },

  /**
   * Chat with copilot to build/validate the story for the customer's draft order.
   * Saves user message and copilot response to the draft, returns the updated Order.
   */
  async chatDraft(message: string, replaceLastUserMessage?: boolean) {
    return fetchApi<Order>("/customer/orders/draft/chat", {
      method: "POST",
      body: JSON.stringify({ message, replaceLastUserMessage }),
    });
  },

  /**
   * Chat with copilot to build/validate the story for an anonymous draft order.
   * Saves user message and copilot response to the draft, returns the updated OrderDraft.
   */
  async chatAnonymousDraft(
    anonymousId: string,
    message: string,
    replaceLastUserMessage?: boolean,
  ) {
    return fetchApi<OrderDraft>(`/anonymous/orders/draft/${anonymousId}/chat`, {
      method: "POST",
      body: JSON.stringify({ message, replaceLastUserMessage }),
    });
  },

  /**
   * Update marketing consent for an order
   */
  async updateMarketingConsent(orderId: number) {
    return fetchApi<void>(`/customer/orders/${orderId}/marketing-consent`, {
      method: "PUT",
    });
  },

  /**
   * Submit a review for an order
   */
  async submitReview(orderId: number, review: OrderReviewSubmitRequest) {
    return fetchApi<void>(`/customer/orders/${orderId}/review`, {
      method: "POST",
      body: JSON.stringify(review),
    });
  },
};
